import { useCallback, type MouseEvent as ReactMouseEvent } from 'react';

import { GridRowParams, MuiEvent } from '@mui/x-data-grid-pro';
import { useSearchParams } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';

import { useColumns } from './hooks/useColumns';
import { DASHBOARD_ID_PARAMETER_NAME } from '../../constants';
import { Dashboard } from '@/api/typesense/dashboards';
import {
  BaseDataTable,
  BaseDataTableProps,
} from '@/components/ui/BaseDataTable';

type DashboardsTableProps = Omit<BaseDataTableProps<Dashboard>, 'getRowId'> & {
  totalResults: number;
  getRowId?: (row: Dashboard) => number;
  isPanelOpen?: boolean;
  onPanelOpen?: () => void;
  onPanelOpenAndFocus?: () => void;
};

export const DashboardsTable = ({
  getRowId = (r: Dashboard) => r.SourceId,
  columns: columnsProp,
  columnWidths,
  isPanelOpen = false,
  onPanelOpen,
  onPanelOpenAndFocus,
  totalResults,
  ...restProps
}: DashboardsTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDashboardId = searchParams.get(DASHBOARD_ID_PARAMETER_NAME);

  // Handle dashboard selection by updating URL params
  const handleViewDetails = (
    dashboardId: string,
    options?: { replace?: boolean },
  ) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set(DASHBOARD_ID_PARAMETER_NAME, dashboardId);
      return newParams;
    }, options);
  };

  const defaultColumns = useColumns(columnWidths);
  const columns = columnsProp ?? defaultColumns;

  const handleRowClick = (
    params: GridRowParams<Dashboard>,
    event: MuiEvent<ReactMouseEvent<HTMLElement>>,
  ) => {
    // Don't trigger row click when clicking on interactive elements (links, buttons, etc.)
    const target = event.target as HTMLElement;
    if (target.closest('a, button')) {
      return;
    }

    const clickedId = String(params.row.SourceId);

    handleViewDetails(clickedId);
    onPanelOpen?.();
  };

  // Arrow key navigation - only active when panel is open, debounced to prevent API spam
  const handleRowNavigate = useDebouncedCallback(
    (row: Dashboard) => {
      if (!isPanelOpen) return;
      handleViewDetails(String(row.SourceId), { replace: true });
    },
    100, // 100ms debounce to avoid spamming when holding down arrow keys
    { leading: false, trailing: true },
  );

  // Ctrl+Enter and Ctrl+Shift+Enter toggle behavior
  const handleRowToggle = useCallback(
    (row: Dashboard, options?: { shouldFocus: boolean }) => {
      const clickedId = String(row.SourceId);
      handleViewDetails(clickedId);

      if (options?.shouldFocus) {
        if (onPanelOpenAndFocus) {
          onPanelOpenAndFocus();
        } else {
          onPanelOpen?.();
        }
      } else {
        onPanelOpen?.();
      }
    },
    [handleViewDetails, onPanelOpen, onPanelOpenAndFocus],
  );

  return (
    <BaseDataTable<Dashboard>
      getRowId={getRowId}
      onRowClick={handleRowClick}
      onRowNavigate={handleRowNavigate}
      onRowToggle={handleRowToggle}
      columns={columns}
      columnWidths={columnWidths}
      selectedRowId={isPanelOpen ? selectedDashboardId : null}
      data-automation-id="dashboard-overview-data-grid"
      pinnedColumns={{ left: ['DashboardType', 'Name'] }}
      hasRowNavigation
      totalResults={totalResults}
      {...restProps}
    />
  );
};
